import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LogIn, LogOut, Loader2, FileText, CalendarCheck, Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadBlob, filenameFromHeader, errorMessage } from "@/lib/api";

// IST-based helpers so date/month always align with the school's timezone.
const istNow = () => new Date(Date.now() + 5.5 * 3600 * 1000);
const istToday = () => istNow().toISOString().slice(0, 10);
const istMonth = () => istNow().toISOString().slice(0, 7);
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—";
const STATUS_CLS = {
  present: "bg-emerald-100 text-emerald-700", "half-day": "bg-amber-100 text-amber-700",
  leave: "bg-sky-100 text-ark-skyDeep", absent: "bg-red-100 text-red-600",
};

const StaffSelfPanel = () => {
  const [att, setAtt] = useState(null);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(istMonth());
  const today = istToday();

  const load = async (m = month) => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        api.get("/me/attendance", { params: { month: m } }),
        api.get("/me/payslips"),
      ]);
      setAtt(a.data); setSlips(p.data);
    } catch { toast.error("Failed to load your data."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  const todayRec = att?.records?.find((r) => r.date === today);

  const checkIn = async () => {
    setBusy(true);
    try { await api.post("/attendance/checkin"); toast.success("Checked in!"); load(month); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not check in."); }
    finally { setBusy(false); }
  };
  const checkOut = async () => {
    setBusy(true);
    try { await api.post("/attendance/checkout"); toast.success("Checked out!"); load(month); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not check out."); }
    finally { setBusy(false); }
  };

  const downloadMySlip = async (p) => {
    try {
      const res = await api.get(`/me/payslips/${p.id}/pdf`, { responseType: "blob" });
      const name = filenameFromHeader(res.headers, `payslip_${p.month}.pdf`);
      downloadBlob(res.data, name);
    } catch (err) { toast.error(await errorMessage(err, "Could not download PDF.")); }
  };

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" /></div>;

  const s = att?.summary || {};
  return (
    <div className="space-y-6" data-testid="staff-self-panel">
      <Card className="border-0 rounded-3xl shadow-ark-soft bg-gradient-to-br from-sky-50 to-teal-50">
        <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-lg text-ark-ink">Today · {today}</h3>
            <p className="text-sm text-slate-600 mt-1" data-testid="today-status">
              {todayRec ? `In: ${fmtTime(todayRec.check_in)} · Out: ${fmtTime(todayRec.check_out)}` : "Not checked in yet"}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={checkIn} disabled={busy || (todayRec && todayRec.check_in)} data-testid="checkin-btn"
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-display font-semibold">
              <LogIn className="h-4 w-4 mr-2" /> Check In
            </Button>
            <Button onClick={checkOut} disabled={busy || !todayRec?.check_in || todayRec?.check_out} data-testid="checkout-btn"
              variant="outline" className="rounded-full border-ark-teal text-ark-tealDeep font-display font-semibold">
              <LogOut className="h-4 w-4 mr-2" /> Check Out
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 rounded-3xl shadow-ark-soft">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="font-display font-bold text-lg text-ark-ink flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-ark-teal" /> My Attendance · {month}
            </h3>
            <Input type="month" value={month} data-testid="my-attendance-month"
              onChange={(e) => setMonth(e.target.value || istMonth())}
              className="h-11 rounded-2xl border-slate-200 w-full sm:w-48" />
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[["Working", att?.working_days], ["Present", s.present], ["Half-day", s["half-day"]], ["Leave", s.leave], ["Absent", s.absent]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-slate-50 p-3 text-center">
                <div className="font-display font-bold text-xl text-ark-ink">{v || 0}</div>
                <div className="text-xs text-slate-500 font-semibold">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
            {(att?.records || []).map((r) => (
              <div key={r.date} data-testid={`att-record-${r.date}`} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-white border border-slate-100 text-sm">
                <span className="font-semibold text-slate-700 shrink-0">{r.date}</span>
                <span className="text-slate-500 hidden sm:inline">{fmtTime(r.check_in)} – {fmtTime(r.check_out)}</span>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {r.late && <span data-testid={`late-flag-${r.date}`} className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700">LATE</span>}
                  {r.early_leave && <span data-testid={`early-flag-${r.date}`} className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700">EARLY</span>}
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_CLS[r.status] || "bg-slate-100"}`}>{r.status}</span>
                </div>
              </div>
            ))}
            {(!att?.records || att.records.length === 0) && <div className="text-center text-slate-500 py-6">No records this month.</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 rounded-3xl shadow-ark-soft">
        <CardContent className="p-6">
          <h3 className="font-display font-bold text-lg text-ark-ink flex items-center gap-2">
            <FileText className="h-5 w-5 text-ark-skyDeep" /> My Payslips
          </h3>
          <div className="mt-4 space-y-3">
            {slips.length === 0 ? (
              <div className="py-8 text-center text-slate-500" data-testid="my-payslips-empty">No payslips yet.</div>
            ) : slips.map((p) => (
              <div key={p.id} data-testid={`my-payslip-${p.month}`} className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="min-w-0">
                  <div className="font-semibold text-ark-ink">{p.month}</div>
                  <div className="text-xs text-slate-500">{money(p.monthly_salary)} · −{money(p.attendance_deduction)} att · +{money(p.bonus)} · −{money(p.deductions)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="font-display font-bold text-lg text-emerald-600">{money(p.net_pay)}</div>
                  <button
                    onClick={() => downloadMySlip(p)}
                    aria-label={`Download payslip PDF for ${p.month}`}
                    data-testid={`my-payslip-download-${p.month}`}
                    className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:text-ark-skyDeep hover:bg-sky-50 transition">
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffSelfPanel;
